import type { Hotel } from '@elara/client-core/models/HotelModel';

import { useEffect } from 'react';

import { cn, formatAddress } from '@elara/client-core/lib/utils';

import { formatDisplayUrl } from '#lib/url-utils.js';
import { useHotelGallery } from '#lib/use-hotel-gallery.js';
import { useChatInputState, useHotelGuideState, useRouterState, useTripsState } from '#states/hooks.js';
import { Card, CardContent, CardHeader, CardTitle, ShortlistButton } from '#ui';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '#ui/accordion.js';
import { AskElaraButton } from '#ui/AskElaraButton.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { ElaraBenefitsBanner } from '#ui/ElaraBenefitsBanner.js';
import Loader from '#ui/loader.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '#ui/tabs.js';

import { GUIDE_BAND_CLASSES } from './guide-band.js';
import { GuideImageGallery, GuideLayout } from './GuideLayout.js';
import { GuideNotFound } from './GuideNotFound.js';
import { HotelRoomSearch } from './HotelRoomSearch.js';
import { PlanTripCTA } from './PlanTripCTA.js';

import { MapWidget } from '../chat/index.js';

/**
 * The guide's tabs as a plain underline rather than the shared filled pills: a hairline runs the full
 * width of the guide, and the active tab sits on it in dark text with a heavier rule beneath; the rest
 * are muted. `-mb-px` lands each tab's own border on the hairline instead of stacking above it.
 */
// The tabs' rule runs to the guide's edges, past its padding and any width cap: drawn wide and clipped by
// the panel's `overflow-x-hidden`, as a border on the list would stop at the content's edges.
const GUIDE_TABS_LIST_CLASSES = cn(
  'relative flex w-full justify-start gap-6 rounded-none bg-transparent p-0',
  'after:absolute after:-inset-x-[100vw] after:-bottom-px after:h-px after:bg-border',
);
const GUIDE_TAB_CLASSES = cn(
  // `z-[1]` keeps the active tab's underline over the rule, and under the panel's floating header (`z-10`).
  'relative z-[1] -mb-px cursor-pointer rounded-none border-b-2 border-transparent px-0 pt-0 pb-3 text-muted-foreground transition-colors',
  // Hovering an inactive tab darkens it and hints its underline; the active tab's underline is the full one.
  'data-[state=inactive]:hover:border-border data-[state=inactive]:hover:text-foreground',
  'data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground',
);

/** Find rooms on the guide's band, from the tabs' rule (the tabs' `gap-6` above it) to the panel's edges. */
const GUIDE_ROOMS_TAB_CLASSES = cn(GUIDE_BAND_CLASSES, 'mt-0 flex flex-1 flex-col before:-top-6');

interface HotelGuideProps {
  hotelId?: string;
}

export function HotelGuide({ hotelId }: HotelGuideProps) {
  const hotelGuideState = useHotelGuideState();
  const chatInputState = useChatInputState();
  const tripsState = useTripsState();
  const routerState = useRouterState();

  useEffect(() => {
    hotelGuideState.reset();

    if (hotelId) {
      hotelGuideState.findHotel(hotelId);
    }
    // hotelGuideState is a stable reference from DI container, no need to include in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  // Load the FULL gallery eagerly — this is a dedicated hotel view, one gallery read is the
  // expected cost. (Photo backfill for pre-pipeline hotels fires from HotelModel.previewImages.)
  const galleryImages = useHotelGallery(hotelId);

  const hotel: Hotel | undefined = hotelGuideState.hotel;

  tripsState.setPreviewPanelTitle(hotel?.name ?? '');

  if (!hotelId) {
    return <GuideNotFound guide="hotel" title="Hotel not found" />;
  }

  if (hotelGuideState.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader loading={true} />
      </div>
    );
  }

  if (!hotel) {
    return <GuideNotFound guide="hotel" title="Hotel not found" />;
  }

  const mapPoints = hotel.address ? [{ id: '1', address: formatAddress(hotel.address), label: hotel.name }] : [];
  // `#tab=rooms` opens the Rooms tab (ELARA-1092): set by the tab itself, and by "View all rooms"
  // under a chat room card, which also names the search to list (`#search=`).
  const activeTab = routerState.getHashParam('tab') === 'rooms' ? 'rooms' : 'overview';

  const images = galleryImages ?? hotel.previewImages;
  // The gallery belongs to Overview, so the tabs sit directly under the title and Rooms opens without it.
  const overview = (
    <div className="flex flex-col gap-8">
      {images && images.length > 0 && <GuideImageGallery images={images} />}
      <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-8 items-start">
        {/* Description */}
        <div className="flex flex-col items-start gap-4">
          {hotel.long_description && (
            <SimpleMarkdown
              text={hotel.long_description}
              className="text-pretty max-w-prose [&>p:first-child]:text-lg [&>p:first-child]:leading-[145%]"
            />
          )}
        </div>

        {/* Map */}
        {mapPoints.length > 0 && <MapWidget points={mapPoints} size="full" zoom={16} className="my-0 min-h-96" />}

        {/* Elara Benefits, across both columns under the description and map */}
        {hotel.isSerandipiansAffiliated && <ElaraBenefitsBanner hotel={hotel} className="@3xl:col-span-2" />}

        {/* Amenities */}
        {/* <div className="col-span-1 @3xl:col-span-2 flex flex-wrap gap-3 w-full">
            {hotel.amenities?.map((amenity, index) => (
              <div
                key={`${amenity}-${index}`}
                className="flex items-center gap-2 border px-3 py-2 rounded-sm whitespace-nowrap text-sm"
              >
                {amenity}
              </div>
            ))}
          </div> */}

        {/* Hotel details */}
        <Card>
          <CardHeader>
            <CardTitle>What to expect</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="w-full">
              {hotel.dining_detail && (
                <AccordionItem value="dining">
                  <AccordionTrigger>
                    <div className="flex items-center gap-3">
                      <MaterialIcon icon="restaurant" /> Dining
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <SimpleMarkdown text={hotel.dining_detail} className="text-pretty max-w-prose" />
                  </AccordionContent>
                </AccordionItem>
              )}
              {hotel.accommodations_detail && (
                <AccordionItem value="accommodations">
                  <AccordionTrigger>
                    <div className="flex items-center gap-3">
                      <MaterialIcon icon="bed" /> Accommodation
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <SimpleMarkdown text={hotel.accommodations_detail} className="text-pretty max-w-prose" />
                  </AccordionContent>
                </AccordionItem>
              )}
              {hotel.experiences_detail && (
                <AccordionItem value="experiences">
                  <AccordionTrigger>
                    <div className="flex items-center gap-3">
                      <MaterialIcon icon="explore" /> Experiences
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <SimpleMarkdown text={hotel.experiences_detail} className="text-pretty max-w-prose" />
                  </AccordionContent>
                </AccordionItem>
              )}
              {hotel.family_detail && (
                <AccordionItem value="family">
                  <AccordionTrigger>
                    <div className="flex items-center gap-3">
                      <MaterialIcon icon="family_restroom" /> Families
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <SimpleMarkdown text={hotel.family_detail} className="text-pretty max-w-prose" />
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </CardContent>
        </Card>

        {/* Reviews */}
        {/* <Card>
            <CardHeader>
              <CardTitle>Reviews</CardTitle>
            </CardHeader>
            <CardContent></CardContent>
          </Card> */}

        {/* Contact information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-[16px_auto] gap-4 items-start">
              {hotel.contact?.email && (
                <>
                  <div className="flex items-center gap-2 mt-1">
                    <MaterialIcon icon="mail" size={IconSizes.xs} />
                  </div>
                  <a href={`mailto:${hotel.contact!.email}`} target="_blank" rel="noopener noreferrer" className="link">
                    {hotel.contact.email}
                  </a>
                </>
              )}

              {hotel.contact?.phone && (
                <>
                  <div className="flex items-center gap-2 mt-1">
                    <MaterialIcon icon="call" size={IconSizes.xs} />
                  </div>
                  <a href={`tel:${hotel.contact!.phone}`} target="_blank" rel="noopener noreferrer" className="link">
                    {hotel.contact.phone}
                  </a>
                </>
              )}

              {hotel.website && (
                <>
                  <div className="flex items-center gap-2 mt-1">
                    <MaterialIcon icon="public" size={IconSizes.xs} />
                  </div>
                  <a href={hotel.website!} target="_blank" rel="noopener noreferrer" className="link truncate">
                    {formatDisplayUrl(hotel.website)}
                  </a>
                </>
              )}

              {hotel.address && (
                <>
                  <div className="flex items-center gap-2 mt-1">
                    <MaterialIcon icon="location_on" size={IconSizes.xs} />
                  </div>
                  <span>
                    {formatAddress(hotel.address)}{' '}
                    <a href={hotel.mapUrl} target="_blank" rel="noopener noreferrer" className="link ml-1">
                      View map ↗
                    </a>
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <GuideLayout
      title={hotel.name ?? ''}
      metadata={
        <>
          {hotel.address && (
            <div className="flex flex-col @3xl:flex-row items-start @3xl:items-center gap-1 @3xl:gap-2">
              <div className="flex items-center gap-1">
                <MaterialIcon icon="location_on" size={IconSizes.xs} /> {hotel.address?.city}, {hotel.address?.country}
              </div>
            </div>
          )}
        </>
      }
      actions={
        tripsState.current ? (
          <>
            <AskElaraButton
              onClick={() => {
                const id = hotel.id || hotelId;
                if (id) {
                  chatInputState.addDataAttachment('hotel', id, hotel.name ?? '');

                  tripsState.switchToPanel('chat');
                  tripsState.closePreviewPanel();

                  chatInputState.focusInput();
                }
              }}
            />
            <ShortlistButton itemType="hotel" referenceId={hotel.id || hotelId!} title={hotel.name ?? ''} />
          </>
        ) : (
          // Opened outside a trip (e.g. from a Story): the actions above all need a current trip.
          <PlanTripCTA type="hotel" id={hotel.id || hotelId!} name={hotel.name ?? undefined} />
        )
      }
    >
      {tripsState.current ? (
        // Rooms are searched and saved under a trip, so the Rooms tab exists only inside one (ELARA-1092).
        <Tabs
          value={activeTab}
          // The tab is the guide stack entry's view, so Back from a guide opened on top of this one returns
          // to it. Back on Overview the Rooms view's search link goes too: the view is replaced whole.
          onValueChange={(tab) =>
            tripsState.replaceGuide('hotel', hotelId, tab === 'rooms' ? { tab: 'rooms' } : undefined)
          }
          className="flex flex-1 flex-col gap-6"
        >
          <TabsList className={GUIDE_TABS_LIST_CLASSES}>
            <TabsTrigger value="overview" className={GUIDE_TAB_CLASSES}>
              Overview
            </TabsTrigger>
            <TabsTrigger value="rooms" className={GUIDE_TAB_CLASSES}>
              Find rooms
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-0">
            {overview}
          </TabsContent>
          <TabsContent value="rooms" className={GUIDE_ROOMS_TAB_CLASSES}>
            <HotelRoomSearch hotelId={hotel.id || hotelId} searchId={routerState.getHashParam('search')} />
          </TabsContent>
        </Tabs>
      ) : (
        overview
      )}
    </GuideLayout>
  );
}
